/**
 * Operations orders, missions 33-36: sabotage and endurance (10 §2). Each states its special rule as an
 * order, names the equipment the job needs, and says how it ends - for 36, that the Osprey comes on its own.
 * tests/unit/uiBriefing.test.ts holds a phrase from every rule here and must be kept in step.
 */

import type { ArchetypeOrder } from './orders';
import { fixed, NO_NOISE_RULE, NOTHING_SPECIAL, ON_THE_CLOCK, QUIET_PISTOL, rules } from './orderKit';

type SabotageId = 'power-station' | 'mine-road' | 'beacon' | 'hold-crossing';

export const SABOTAGE_ORDERS: Readonly<Record<SabotageId, ArchetypeOrder>> = {
  'power-station': {
    situation: 'A POWER STATION. THREE BREAKER PANELS IN ITS SWITCH YARD FEED THE SEARCHLIGHTS AND THE AUTOMATED'
      + ' MINI-BUNKERS ACROSS THE AREA.',
    mission: fixed('THROW ALL THREE BREAKERS.'),
    execution: [
      'AT EACH PANEL, HOLD INTERACT FOR A FEW SECONDS, STANDING STILL. MOVE OR TAKE A HIT AND YOU START AGAIN.',
    ],
    special: rules(
      'EACH BREAKER DARKENS PART OF THE MAP: ITS SEARCHLIGHTS GO OUT AND ITS MINI-BUNKERS GO DEAD. THE JOB GETS'
        + ' EASIER AS YOU GO. THROW FIRST THE BREAKER THAT BLINDS YOUR NEXT APPROACH.',
      'THERE IS NO KNIFE RULE, BUT THE LIGHTS ARE STILL ON WHEN YOU ARRIVE: A QUIET WALK TO THE FIRST BREAKER PAYS.',
    ),
    equipment: [NOTHING_SPECIAL],
  },
  'mine-road': {
    situation: 'A ROAD THE ENEMY COLUMN WILL USE, WITH THREE MARKED CULVERTS UNDER IT. THE COLUMN ARRIVES AT A'
      + ' STATED TIME.',
    mission: fixed('LAY A CHARGE AT EACH MARKED CULVERT BEFORE THE COLUMN ARRIVES, AND LEAVE WITHOUT ANYONE KNOWING'
      + ' YOU CAME.'),
    execution: [
      'ONE TIME BOMB AT EACH CULVERT. THE CHARGES WAIT FOR THE COLUMN: WHATEVER FUSE YOU CHOOSE, THEY GO OFF UNDER'
        + ' IT AS IT PASSES OVER THEM, AND NOT BEFORE. BE WELL CLEAR OF THE ROAD BY THEN.',
      'A CHARGE LAID ANYWHERE BUT AT A MARKED CULVERT IS WASTED.',
    ],
    special: rules(
      'IF YOU ARE SEEN WITHIN 20 M OF ANY CULVERT - BEFORE OR AFTER LAYING - THE CHARGES ARE FOUND AND THE MISSION'
        + ' HAS FAILED.',
      `THE COLUMN ARRIVES AT A STATED TIME. ${ON_THE_CLOCK} ANY CULVERT NOT MINED WHEN IT ARRIVES IS A FAILED`
        + ' MISSION.',
    ),
    equipment: [
      'ONE TIME BOMB FOR EACH CULVERT: AT LEAST THREE. YOU CARRY ONE. PACK THE REST IN THE PODS. NO BOMBS, NO MISSION.',
    ],
    time: "THE COLUMN'S ARRIVAL IS THE CLOCK THAT MATTERS.",
  },
  beacon: {
    situation: 'AN ENEMY COMPOUND MARKED FOR AN AIR STRIKE. THE STRIKE NEEDS A BEACON ON THE GROUND TO FIND IT.',
    mission: fixed('PLACE THE STRIKE BEACON IN THE CENTRE OF THE COMPOUND, THEN BE 80 M CLEAR BEFORE THE STRIKE'
      + ' LANDS.'),
    execution: [
      'THE STRIKE BEACON RIDES IN POD A. OPEN POD A AND YOU HAVE IT.',
      'AT THE MARKED SPOT IN THE CENTRE OF THE COMPOUND, INTERACT TO PLACE IT. THAT STARTS THE COUNT.',
    ],
    special: rules(
      'THE STRIKE LANDS 60 SECONDS AFTER THE BEACON IS PLACED. BE 80 M CLEAR OF IT BY THEN. INSIDE 80 M THE STRIKE'
        + ' CUTS YOU DOWN AND THE MISSION HAS FAILED. YOUR HUD SHOWS THE COUNT AND THE DISTANCE.',
      'KNIFE ONLY UNTIL THE BEACON IS PLACED: NOT ONE SHOT AND NOT ONE EXPLOSION BEFORE THAT. TREAT A BROKEN SILENCE'
        + ' AS A FAILED MISSION.',
      'POD A MUST BE DROPPED, AND IT MUST LAND INTACT. LEAVE IT ABOARD, OR LOSE IT ON BAD GROUND, AND THE MISSION IS'
        + ' LOST AT THE DROP. PICK OPEN GROUND FOR POD A.',
    ),
    equipment: ['THE BEACON WEIGHS 4 ON YOUR BACK UNTIL IT IS PLACED.', QUIET_PISTOL],
    extraction: [
      'PLAN YOUR WAY OUT BEFORE YOU PLACE THE BEACON: 60 SECONDS IS 80 M AND NOT MUCH MORE IF ANYONE IS SHOOTING.',
      'THEN RECALL AS NORMAL: 45 SECONDS OUT, 20 SECONDS OVER THE X.',
    ],
  },
  'hold-crossing': {
    situation: 'A FORD THE ENEMY NEEDS. WHILE HE IS HELD HERE OUR COLUMN CROSSES ELSEWHERE. HE WILL COME IN WAVES.',
    mission: fixed('REACH THE FORD AND HOLD IT FOR THREE MINUTES, ALIVE, AGAINST WHAT COMES.'),
    execution: [
      'THE FORD IS MARKED. THE THREE MINUTES COUNT ONLY WHILE YOU ARE INSIDE IT. LEAVING THE FORD STOPS THE CLOCK;'
        + ' IT DOES NOT RESET IT.',
      'THE WAVES START WHEN YOU REACH THE FORD, NOT BEFORE: THE FIRST SETS OUT HALF A MINUTE AFTER YOU ARRIVE, THE'
        + ' REST THROUGH THE THREE MINUTES. EACH COUNTDOWN IS ON YOUR HUD FROM THE MOMENT YOU ARE AT THE FORD. USE THE'
        + ' GAPS TO REACH A POD AND GET BACK.',
      'DROP YOUR PODS CLOSE TO THE FORD. THIS IS A FIGHT, AND YOU WILL RUN DRY.',
    ],
    special: rules(
      'RECALL IS DISABLED. THE OSPREY COMES ON ITS OWN WHEN THE THREE MINUTES ARE HELD, AND NOT BEFORE. THERE IS NO'
        + ' ABORT ON THIS ONE.',
      NO_NOISE_RULE,
    ),
    equipment: ['AMMUNITION, GRENADES AND FIRST AID BEFORE ANYTHING ELSE. NOTHING HERE NEEDS A BOMB.'],
    extraction: [
      'RECALL IS DISABLED. WHEN THE FORD HAS BEEN HELD THE OSPREY COMES ON ITS OWN, AS IF YOU HAD CALLED IT: 45'
        + ' SECONDS OUT, 20 SECONDS OVER THE X.',
      'YOU STILL HAVE TO REACH THE LADDER, AND THE ENEMY WILL STILL BE THERE.',
    ],
    time: 'THREE MINUTES IN THE FORD, PLUS THE WALK THERE. DO NOT LOITER ON THE WAY.',
  },
};
